/**
 * Centralized analytics configuration.
 *
 * Holds GA4 measurement ID, Google Ads conversion ID, and per-event
 * conversion labels used by src/lib/analytics.ts conversion helpers.
 *
 * The GA4 ID is duplicated in public/google-analytics-bootstrap.js because
 * the bootstrap runs in <head> before the app bundle loads. assertGa4Matches()
 * verifies the two are in sync at app init.
 */

export const GA4_MEASUREMENT_ID = 'G-K7JTTC75KT';

export const GOOGLE_ADS_ID = 'AW-18062073519';

export const CONVERSION_LABELS = {
  /** Client-side Google tag website conversion fired from /welcome on first signup. */
  signup: 'NepSCMW7hJ4cEK-91aRD',
  /** Offline conversion uploaded via the Google Ads API when checkout.session.completed sets trial. Numeric resource ID. */
  beginTrialOffline: '7579122178',
  /** Client-side Google tag website conversion fired from /purchase-success for non-trial purchases. */
  purchaseWeb: '0gYiCO2mgZ4cEK-91aRD',
  /** Offline conversion uploaded via the Google Ads API on invoice.paid (trial-to-paid). Numeric resource ID. */
  purchaseOffline: '7579194369',
} as const;

/** Full send_to string for the signup website conversion. */
export const SIGNUP_SEND_TO = `${GOOGLE_ADS_ID}/${CONVERSION_LABELS.signup}`;

/** Full send_to string for the purchase (web) website conversion. */
export const PURCHASE_WEB_SEND_TO = `${GOOGLE_ADS_ID}/${CONVERSION_LABELS.purchaseWeb}`;

/**
 * Assert the GA4 measurement ID baked into the bootstrap script matches this
 * config. Called at app init. Logs a warning rather than throwing so a stale
 * bootstrap never bricks the app.
 */
export function assertGa4Matches(): void {
  const bootstrapId = typeof window !== 'undefined' ? window.__fabGaMeasurementId : undefined;
  if (!bootstrapId) {
    return;
  }
  if (bootstrapId !== GA4_MEASUREMENT_ID) {
    console.warn(
      '[analytics] GA4 measurement ID drift:',
      { bootstrap: bootstrapId, config: GA4_MEASUREMENT_ID },
    );
  }
}

declare global {
  interface Window {
    __fabGaMeasurementId?: string;
  }
}
